import { type Dispatch, type ReactNode, type SetStateAction, useCallback, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ScanSearch } from 'lucide-react';
import { useEcho } from '@laravel/echo-react';
import { uniq } from 'lodash';
import { type ChildNodeType, DAMNodeType, NodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { useFlattenedSearchResults, useFlattenedTree } from '@/hooks/data/tree/useFlattenedTree';
import { MIN_TREE_SEARCH_LENGTH } from '@/hooks/data/tree/useTreeSearch';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { getFolderWithAncestors } from '@/services/folder.service';
import { getAlbumWithAncestors } from '@/services/album.service';
import { TreeHome } from '@/components/sidebar/tree/tree-home';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { Branch } from '@/components/sidebar/tree/branch';
import { useTreeContext } from '@/context/TreeContext';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export enum TreeMode {
  SelectFolder = 'select-folder',
  SelectAlbum = 'select-album',
}

export const DEPTH_PADDING = 20;

type TreeProps = {
  className?: ReactNode;
  openIds: Record<string, boolean>;
  setOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
  tree: Array<TreeRecursiveChildrenNode>;
  mode?: TreeMode;
  scrollToNodeId?: string;
  onScrollToNode?: (id: string) => void;
  searchResults?: Array<TreeRecursiveChildrenNode>;
  isSearching?: boolean;
  isSearchLoading?: boolean;
  searchQuery?: string;
  disableDrag?: boolean;
};

export const Tree = ({
  className,
  openIds,
  setOpenIds,
  tree,
  mode,
  scrollToNodeId,
  onScrollToNode,
  searchResults,
  isSearching,
  isSearchLoading,
  searchQuery,
  disableDrag,
}: TreeProps) => {
  const tSearch = useTranslations('search');
  const tTree = useTranslations('tree');
  const setLocateAssetId = useBrowserStore((state) => state.setLocateAssetId);
  const queryClient = useQueryClient();
  const parentRef = useRef<HTMLDivElement>(null);
  // Actions only, via selectors — a bare useUploadStore() would re-render the whole tree
  // body once per upload file chunk for state this component never reads.
  const getActiveUploadSession = useUploadStore((state) => state.getActiveUploadSession);
  const setUploadFolderPath = useUploadStore((state) => state.setUploadFolderPath);
  const changeFolder = useUploadStore((state) => state.changeFolder);
  const findNodePath = useTreeStore((state) => state.findNodePath);
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const setReusableTreeSelection = useTreeStore((state) => state.setReusableTreeSelection);
  const { setSelectedNodes, setCurrentSelectedType } = useTreeContext();
  const { navigateToSearch, navigateToFolder, navigateToNode } = useDamNavigation();
  const { deselectAllItems } = useGallerySelection();
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const search = useIsSearching();
  const folderAncestorsFn = useAuthenticatedQueryFn(getFolderWithAncestors);
  const albumAncestorsFn = useAuthenticatedQueryFn(getAlbumWithAncestors);

  const toggleNodeHandler = useCallback(
    (clickedId: string) => {
      setLocateAssetId(undefined);
      setOpenIds((prev) => {
        const newOpenIds = { ...prev };
        if (prev[clickedId]) {
          delete newOpenIds[clickedId];
        } else {
          newOpenIds[clickedId] = true;
        }
        return newOpenIds;
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree, { nodeId: clickedId }] });
    },
    [setLocateAssetId, setOpenIds, queryClient],
  );

  useEcho(
    `folder.root.children`,
    '.folder.updated',
    (e: { folder: { id: string; parent_id: string } }) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });

      // Keep inspector in sync when a folder changes
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Folder, e.folder.id] });

      if (e.folder.parent_id) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree, { nodeId: e.folder.parent_id }] });
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch] });
    },
    [queryClient],
  );

  const flattenedTree = useFlattenedTree(tree, openIds, mode);
  const flattenedSearchResults = useFlattenedSearchResults(searchResults);

  const rowVirtualizer = useVirtualizer({
    count: flattenedTree.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 10,
    measureElement: undefined,
  });

  const searchVirtualizer = useVirtualizer({
    count: flattenedSearchResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 10,
    measureElement: undefined,
  });

  const {
    session: { selectedAlbums, uploadFolderPath },
    uploadSessionId,
  } = getActiveUploadSession();

  const handleSelectFolderCallback = useCallback(
    async (treeBranch: TreeRecursiveChildrenNode) => {
      await handleSelectFolder(treeBranch, {
        mode,
        setReusableTreeSelection,
        setReusableTreeSelectedFolder,
        findNodePath,
        changeFolder,
        setUploadFolderPath,
        uploadSessionId,
        uploadFolderPath,
        search,
        navigateToSearch,
        navigateToFolder,
        setOpenIds,
        folderAncestorsFn,
        isSearchResult: false,
      });
    },
    [
      mode,
      findNodePath,
      changeFolder,
      setUploadFolderPath,
      setReusableTreeSelectedFolder,
      search,
      navigateToSearch,
      navigateToFolder,
      setReusableTreeSelection,
      uploadFolderPath,
      uploadSessionId,
      setOpenIds,
      folderAncestorsFn,
    ],
  );

  const handleSelectAlbumCallback = useCallback(
    async (treeBranch: TreeRecursiveChildrenNode) => {
      await handleSelectAlbum(treeBranch, {
        mode,
        deselectAllItems,
        setSelectedNodes,
        setCurrentSelectedType,
        setIsAllSelected,
        findNodePath,
        search,
        navigateToSearch,
        navigateToNode,
        setReusableTreeSelection,
        setOpenIds,
        albumAncestorsFn,
        isSearchResult: false,
      });
    },
    [
      mode,
      deselectAllItems,
      setSelectedNodes,
      setCurrentSelectedType,
      setIsAllSelected,
      findNodePath,
      search,
      navigateToSearch,
      navigateToNode,
      setReusableTreeSelection,
      setOpenIds,
      albumAncestorsFn,
    ],
  );

  const handleSearchSelectFolderCallback = useCallback(
    async (treeBranch: TreeRecursiveChildrenNode) => {
      await handleSelectFolder(treeBranch, {
        mode,
        setReusableTreeSelection,
        setReusableTreeSelectedFolder,
        findNodePath,
        changeFolder,
        setUploadFolderPath,
        uploadSessionId,
        uploadFolderPath,
        search,
        navigateToSearch,
        navigateToFolder,
        setOpenIds,
        folderAncestorsFn,
        isSearchResult: true,
      });
    },
    [
      mode,
      setReusableTreeSelection,
      setReusableTreeSelectedFolder,
      findNodePath,
      changeFolder,
      setUploadFolderPath,
      uploadSessionId,
      uploadFolderPath,
      search,
      navigateToSearch,
      navigateToFolder,
      setOpenIds,
      folderAncestorsFn,
    ],
  );

  const handleSearchSelectAlbumCallback = useCallback(
    async (treeBranch: TreeRecursiveChildrenNode) => {
      await handleSelectAlbum(treeBranch, {
        mode,
        deselectAllItems,
        setSelectedNodes,
        setCurrentSelectedType,
        setIsAllSelected,
        findNodePath,
        search,
        navigateToSearch,
        navigateToNode,
        setReusableTreeSelection,
        setOpenIds,
        albumAncestorsFn,
        isSearchResult: true,
      });
    },
    [
      mode,
      deselectAllItems,
      setSelectedNodes,
      setCurrentSelectedType,
      setIsAllSelected,
      findNodePath,
      search,
      navigateToSearch,
      navigateToNode,
      setReusableTreeSelection,
      setOpenIds,
      albumAncestorsFn,
    ],
  );

  if (isSearching) {
    if (isSearchLoading) {
      return (
        <div className={cn('flex h-full w-full items-center justify-center', mode ? 'py-16' : 'py-6', className)}>
          <span className="flex items-center gap-2 text-sm text-text-gray">
            <Loader2 className="icon-4 animate-spin" />
            {tSearch('searching')}
          </span>
        </div>
      );
    }

    if (!flattenedSearchResults.length) {
      return (
        <div
          className={cn(
            'flex h-full w-full flex-col items-center justify-center gap-y-4 p-6 text-center',
            mode && 'py-9',
            className,
          )}
        >
          <ScanSearch className="icon-10 text-icon-base" strokeWidth={1.5} />
          <p className="line-clamp-5 text-sm text-balance wrap-anywhere text-text-gray">
            {searchQuery?.length && searchQuery.length < MIN_TREE_SEARCH_LENGTH
              ? `Enter at least ${MIN_TREE_SEARCH_LENGTH} characters to search`
              : searchQuery
                ? tTree('noResultsForQuery', { query: searchQuery })
                : tTree('noResults')}
          </p>
        </div>
      );
    }

    return (
      <div ref={parentRef} className={cn('tree group/tree w-full overflow-auto', className)} data-cy="tree-search">
        <Branch
          virtualizer={searchVirtualizer}
          flattenedItems={flattenedSearchResults}
          tree={tree}
          openIds={openIds}
          setOpenIds={setOpenIds}
          toggleNodeHandler={() => {}}
          mode={mode}
          disableDrag={disableDrag}
          scrollToNodeId={scrollToNodeId}
          onScrollToNode={onScrollToNode}
          selectedAlbums={selectedAlbums}
          isSearchResult={true}
          handleSelectFolderCallback={handleSearchSelectFolderCallback}
          handleSelectAlbumCallback={handleSearchSelectAlbumCallback}
        />
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className={cn('tree group/tree w-full gap-y-1.5 overflow-auto pt-1.5', className)}
      data-cy="tree"
    >
      <TreeHome disabled={Boolean(mode)} />
      <Branch
        virtualizer={rowVirtualizer}
        flattenedItems={flattenedTree}
        tree={tree}
        openIds={openIds}
        setOpenIds={setOpenIds}
        toggleNodeHandler={toggleNodeHandler}
        mode={mode}
        disableDrag={disableDrag}
        scrollToNodeId={scrollToNodeId}
        onScrollToNode={onScrollToNode}
        selectedAlbums={selectedAlbums}
        isSearchResult={false}
        handleSelectFolderCallback={handleSelectFolderCallback}
        handleSelectAlbumCallback={handleSelectAlbumCallback}
      />
    </div>
  );
};

const handleSelectFolder = async (
  treeBranch: TreeRecursiveChildrenNode,
  {
    mode,
    setReusableTreeSelection,
    setReusableTreeSelectedFolder,
    findNodePath,
    changeFolder,
    setUploadFolderPath,
    uploadSessionId,
    uploadFolderPath,
    search,
    navigateToSearch,
    navigateToFolder,
    setOpenIds,
    folderAncestorsFn,
    isSearchResult,
  }: {
    mode?: TreeMode;
    setReusableTreeSelection: any;
    setReusableTreeSelectedFolder: any;
    findNodePath: any;
    changeFolder: any;
    setUploadFolderPath: any;
    uploadSessionId: string;
    uploadFolderPath: string;
    search: any;
    navigateToSearch: any;
    navigateToFolder: any;
    setOpenIds?: Dispatch<SetStateAction<Record<string, boolean>>>;
    folderAncestorsFn: any;
    isSearchResult: boolean;
  },
) => {
  const {
    node: { name, id: folderId, slug },
  } = treeBranch;

  setReusableTreeSelection({ id: folderId, type: DAMNodeType.Folder, label: name });

  let fallbackSlugPath: Array<string> | undefined;
  let fallbackNamePath: Array<string> | undefined;

  if (isSearchResult && setOpenIds) {
    try {
      const folderDetails = await folderAncestorsFn({ folderId });
      const orderedAncestors = [...(folderDetails.ancestors ?? [])].reverse();

      const baseSelection = { id: folderId, type: DAMNodeType.Folder, label: name };
      setReusableTreeSelection(baseSelection);

      const ancestorIds =
        orderedAncestors?.map((ancestor) => ancestor.id).filter((value): value is string => Boolean(value)) ?? [];

      const ancestorSlugs =
        orderedAncestors?.map((ancestor) => ancestor.slug).filter((value): value is string => Boolean(value)) ?? [];

      const ancestorNames =
        orderedAncestors?.map((ancestor) => ancestor.name).filter((value): value is string => Boolean(value)) ?? [];

      fallbackSlugPath = [...ancestorSlugs, folderDetails.slug ?? slug].filter((value): value is string =>
        Boolean(value),
      );
      fallbackNamePath = [...ancestorNames, folderDetails.name ?? name].filter((value): value is string =>
        Boolean(value),
      );

      const idsToOpen = uniq([...ancestorIds, folderId]);

      if (idsToOpen.length) {
        setOpenIds((prev) => {
          const newOpenIds = { ...prev };
          idsToOpen.forEach((idToOpen) => {
            newOpenIds[idToOpen] = true;
          });
          return newOpenIds;
        });
      }
    } catch (error) {
      errorLog('Action failed:', error);
    }
  }

  if (mode) {
    if (mode === TreeMode.SelectFolder) {
      const pathArray = findNodePath(folderId, 'name') ?? fallbackNamePath;
      const path = pathArray?.join('/');

      // Committing the folder without its path leaves the destination reading as this folder while
      // the files still go to the previous one, so the two move together or not at all.
      if (!path) {
        return;
      }

      changeFolder(uploadSessionId, uploadFolderPath, `/${path}`);
      setUploadFolderPath(uploadSessionId, `/${path}`);
      setReusableTreeSelectedFolder(folderId);
    }
  } else if (search) {
    navigateToSearch(folderId, DAMNodeType.Folder, fallbackSlugPath);
  } else {
    void navigateToFolder(folderId, name, fallbackSlugPath);
  }
};

const handleSelectAlbum = async (
  treeBranch: TreeRecursiveChildrenNode,
  {
    mode,
    deselectAllItems,
    setSelectedNodes,
    setCurrentSelectedType,
    setIsAllSelected,
    findNodePath,
    search,
    navigateToSearch,
    navigateToNode,
    setReusableTreeSelection,
    setOpenIds,
    albumAncestorsFn,
    isSearchResult,
  }: {
    mode?: TreeMode;
    deselectAllItems: any;
    setSelectedNodes: any;
    setCurrentSelectedType: any;
    setIsAllSelected: any;
    findNodePath: any;
    search: any;
    navigateToSearch: any;
    navigateToNode: (id: string, name: string, type: ChildNodeType, path?: Array<string>) => Promise<void>;
    setReusableTreeSelection: any;
    setOpenIds?: Dispatch<SetStateAction<Record<string, boolean>>>;
    albumAncestorsFn: any;
    isSearchResult: boolean;
  },
) => {
  const {
    id,
    node: { name, id: albumId, slug },
  } = treeBranch;

  if (!mode) {
    let fallbackSlugPath: Array<string> | undefined;

    if (isSearchResult && setOpenIds) {
      try {
        const albumDetails = await albumAncestorsFn({ albumId });
        const orderedAncestors = [...(albumDetails.ancestors ?? [])].reverse();

        const ancestorIds =
          orderedAncestors?.map((ancestor) => ancestor.id).filter((value): value is string => Boolean(value)) ?? [];

        const ancestorSlugs =
          orderedAncestors?.map((ancestor) => ancestor.slug).filter((value): value is string => Boolean(value)) ?? [];

        fallbackSlugPath = [...ancestorSlugs, albumDetails.slug ?? slug].filter((value): value is string =>
          Boolean(value),
        );

        if (ancestorIds.length) {
          setOpenIds((prev) => {
            const newOpenIds = { ...prev };
            ancestorIds.forEach((ancestorId) => {
              newOpenIds[ancestorId] = true;
            });
            return newOpenIds;
          });
        }
      } catch (error) {
        errorLog('Action failed:', error);
      }
    }

    if (search) {
      deselectAllItems();
      setSelectedNodes([{ id, name, type: DAMNodeType.Album }]);
      setCurrentSelectedType(NodeType.Albums);
      setIsAllSelected(false);
      navigateToSearch(albumId, DAMNodeType.Album, fallbackSlugPath);
    } else {
      const pathArray = findNodePath(albumId) ?? fallbackSlugPath;
      void navigateToNode(albumId, name, DAMNodeType.Album, pathArray ?? undefined);
    }
    return;
  }

  if (mode === TreeMode.SelectAlbum) {
    setReusableTreeSelection({ id: albumId, type: DAMNodeType.Album, label: name });
  }
};
